import { useState } from 'react'
import { getPartById } from '../catalog/part-catalog'
import type { StudioEnvironment } from '../domain/assembly'
import {
  findMotorDrives,
  motorControlFor,
  motorOutputRpm,
  motorRpm,
  orderedCubes,
  type MotorDirection,
} from '../domain/motor-control'
import {
  analyzeGroundContact,
  analyzeStructure,
  connectedAssemblyForPart,
  summarizeRobotCondition,
} from '../domain/simulation-analysis'
import {
  OBSTACLE_PRESETS,
  type ObstacleDraft,
  type SimulationObstaclePreset,
} from '../domain/simulation-obstacle'
import { COURSE_OBSTACLE_SPECS } from '../domain/simulation-course'
import { useStudioStore } from '../store/useStudioStore'

function PanelHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="panel-heading mode-panel-heading">
      <div><strong>{title}</strong><small>{subtitle}</small></div>
    </div>
  )
}

const simulationEnvironments = [
  {
    id: 'flat',
    eyebrow: '기본 주행',
    title: '기본 평면',
    description: '직진·회전과 조립 상태를 빠르게 확인합니다.',
  },
  {
    id: 'ramp',
    eyebrow: '경사 주행',
    title: '12° 경사로',
    description: '오르막 접지와 자세 변화를 확인합니다.',
  },
  {
    id: 'road',
    eyebrow: '폐쇄형 서킷',
    title: '레이싱 트랙',
    description: '연속 코너·연석·출발선을 갖춘 전용 트랙에서 조향과 고속 주행을 시험합니다.',
  },
  {
    id: 'obstacle',
    eyebrow: '3개 장거리 레일',
    title: '종합 테스트 코스',
    description: '종합·좌우 요철·넓은 틈과 급경사를 잇는 우당탕 레일을 함께 시험합니다.',
  },
] as const satisfies ReadonlyArray<{
  id: StudioEnvironment
  eyebrow: string
  title: string
  description: string
}>

export function MissionSidebar({ experience = 'autonomous' }: { experience?: 'autonomous' | 'mission' }) {
  const environment = useStudioStore((state) => state.environment)
  const setEnvironment = useStudioStore((state) => state.setEnvironment)
  const obstacles = useStudioStore((state) => state.simulationObstacles)
  const addObstacle = useStudioStore((state) => state.addSimulationObstacle)
  const removeObstacle = useStudioStore((state) => state.removeSimulationObstacle)
  const [environmentOpen, setEnvironmentOpen] = useState(true)
  const [obstacleBuilderOpen, setObstacleBuilderOpen] = useState(false)
  const [draft, setDraft] = useState<ObstacleDraft>({
    preset: 'small',
    sizeCm: { ...OBSTACLE_PRESETS.small.sizeCm },
    massG: OBSTACLE_PRESETS.small.massG,
  })
  const selectPreset = (preset: Exclude<SimulationObstaclePreset, 'custom'>) => {
    const definition = OBSTACLE_PRESETS[preset]
    setDraft({
      preset,
      sizeCm: { ...definition.sizeCm },
      massG: definition.massG,
    })
  }
  const setSize = (axis: 'x' | 'y' | 'z', value: number) => {
    setDraft((current) => ({
      ...current,
      preset: 'custom',
      sizeCm: { ...current.sizeCm, [axis]: value },
    }))
  }
  const activeEnvironment = simulationEnvironments.find((option) => option.id === environment)!

  if (experience === 'mission') {
    return (
      <aside className="parts-panel mode-sidebar mission-course-sidebar">
        <PanelHeading title="미션 코스" subtitle="STAGE 01 · 고정 코스" />
        <section className="mission-objective-card">
          <span>MISSION</span>
          <strong>첫 번째 장애물</strong>
          <p>직접 조립한 로봇으로 장애물을 밀어내고 도착선까지 이동하세요.</p>
          <dl>
            <div><dt>부품 제한</dt><dd>최대 8개</dd></div>
            <div><dt>주행 코스</dt><dd>종합 장애물 코스</dd></div>
            <div><dt>완료 조건</dt><dd>도착선 통과</dd></div>
          </dl>
        </section>
        <section className="mission-rules-card">
          <strong>스테이지 규칙</strong>
          <p><i>1</i> 완성 로봇 불러오기 없이 직접 조립</p>
          <p><i>2</i> 제한된 부품으로 주행 가능한 구조 완성</p>
          <p><i>3</i> 시뮬레이션에서 코스 목표 달성</p>
        </section>
      </aside>
    )
  }

  return (
    <aside className="parts-panel mode-sidebar">
      <PanelHeading title="주행 설정" subtitle="코스·장애물 구성" />
      <details
        className="environment-picker"
        aria-label="주행 코스 목록"
        open={environmentOpen}
        onToggle={(event) => setEnvironmentOpen(event.currentTarget.open)}
      >
        <summary aria-label="주행 코스 열고 닫기">
          <span><strong>주행 코스</strong><small>{activeEnvironment.title}</small></span>
          <em>{simulationEnvironments.length}개 코스</em>
        </summary>
        <div className="environment-options">
          {simulationEnvironments.map((option, index) => (
            <button
              className={environment === option.id ? 'selected' : ''}
              aria-pressed={environment === option.id}
              key={option.id}
              onClick={() => setEnvironment(option.id)}
            >
              <i>{index + 1}</i>
              <span>
                <small>{option.eyebrow}</small>
                <strong>{option.title}</strong>
                <p>{option.description}</p>
              </span>
              {environment === option.id && <b>사용 중</b>}
            </button>
          ))}
        </div>
        {environment === 'obstacle' && (
          <section className="course-obstacle-masses" aria-label="기본 장애물 무게">
            <strong>코스 기본 장애물</strong>
            <div>
              {COURSE_OBSTACLE_SPECS.map((obstacle) => (
                <span key={obstacle.key}>
                  <i aria-hidden="true" />
                  {obstacle.label}
                  <b>{Math.round(obstacle.massKg * 1000)}g</b>
                </span>
              ))}
            </div>
            <small>로봇 무게와 바퀴 접지력에 따라 밀리는 거리가 달라집니다.</small>
          </section>
        )}
      </details>

      <details
        className="environment-picker obstacle-builder-picker"
        aria-label="장애물 추가 설정"
        open={obstacleBuilderOpen}
        onToggle={(event) => setObstacleBuilderOpen(event.currentTarget.open)}
      >
        <summary aria-label="장애물 추가 열고 닫기">
          <span><strong>장애물 추가</strong><small>크기·무게 설정</small></span>
          <em>{obstacles.length}개 배치</em>
        </summary>
        <div className="obstacle-builder">
          <div className="obstacle-presets">
            {(Object.keys(OBSTACLE_PRESETS) as Exclude<SimulationObstaclePreset, 'custom'>[]).map((preset) => (
              <button
                className={draft.preset === preset ? 'active' : ''}
                key={preset}
                onClick={() => selectPreset(preset)}
              >
                <b>{OBSTACLE_PRESETS[preset].label}</b>
                <small>{OBSTACLE_PRESETS[preset].massG}g</small>
              </button>
            ))}
          </div>
          <div className="obstacle-size-fields">
            {([
              ['x', '가로'],
              ['y', '높이'],
              ['z', '깊이'],
            ] as const).map(([axis, label]) => (
              <label key={axis}>
                <span>{label}</span>
                <input
                  aria-label={`장애물 ${label}`}
                  type="number"
                  min="1"
                  max="50"
                  value={draft.sizeCm[axis]}
                  onChange={(event) => setSize(axis, event.currentTarget.valueAsNumber)}
                />
                <small>cm</small>
              </label>
            ))}
          </div>
          <label className="obstacle-mass">
            <span>무게</span>
            <input
              aria-label="장애물 무게"
              type="number"
              min="1"
              max="20000"
              value={draft.massG}
              onChange={(event) => setDraft((current) => ({
                ...current,
                preset: 'custom',
                massG: event.currentTarget.valueAsNumber,
              }))}
            />
            <small>g</small>
          </label>
          <button className="add-obstacle" onClick={() => addObstacle(draft)}>이 설정으로 추가</button>
          <div className="obstacle-list">
            {obstacles.map((obstacle) => (
              <div key={obstacle.id}>
                <span><b>{obstacle.label}</b><small>{Math.round(obstacle.size.x * 100)}×{Math.round(obstacle.size.y * 100)}×{Math.round(obstacle.size.z * 100)}cm · {Math.round(obstacle.massKg * 1000)}g</small></span>
                <button aria-label={`${obstacle.label} 제거`} onClick={() => removeObstacle(obstacle.id)}>×</button>
              </div>
            ))}
            {!obstacles.length && <p>추가한 장애물이 없습니다.</p>}
          </div>
        </div>
      </details>
    </aside>
  )
}

export function SimulationSidebar() {
  const isSimulating = useStudioStore((state) => state.isSimulating)
  const placedParts = useStudioStore((state) => state.placedParts)
  const connections = useStudioStore((state) => state.connections)
  const selectedInstanceId = useStudioStore((state) => state.selectedInstanceId)
  const motorControls = useStudioStore((state) => state.motorControls)
  const runtimeMotorControls = useStudioStore((state) => state.runtimeMotorControls)
  const setMotorDirection = useStudioStore((state) => state.setMotorDirection)
  const setMotorSpeed = useStudioStore((state) => state.setMotorSpeed)
  const selectedAssembly = connectedAssemblyForPart(
    placedParts,
    connections,
    selectedInstanceId,
  )
  const selectedIds = new Set(selectedAssembly.map((part) => part.instanceId))
  const selectedConnections = connections.filter((connection) =>
    selectedIds.has(connection.fromPartId) && selectedIds.has(connection.toPartId))
  const cubes = orderedCubes(selectedAssembly)
  const drives = findMotorDrives(selectedAssembly, selectedConnections)
  const drivenParts = new Map(selectedAssembly.map((part) => [part.instanceId, part]))
  const drivesByCube = new Map(drives.map((drive) => [drive.cubeInstanceId, drive]))
  const structure = analyzeStructure(selectedAssembly, selectedConnections)
  const groundContact = analyzeGroundContact(selectedAssembly)
  const detachedCount = structure.parts.filter((part) => part.status === 'detached').length
  const fragileNames = structure.parts
    .filter((part) => structure.fragilePartIds.includes(part.instanceId))
    .map((analysis) => {
      const part = selectedAssembly.find((item) => item.instanceId === analysis.instanceId)
      const name = part ? getPartById(part.partId)?.name : null
      return name ? `${name} ${Math.round(analysis.retention * 100)}%` : null
    })
    .filter((name): name is string => Boolean(name))
  const conditionSummary = summarizeRobotCondition(structure, groundContact)
  const conditionWarning = detachedCount > 0
    || structure.assemblyScore < 0.65
    || !groundContact.stable
    || groundContact.bodyScrapeLikely
    || groundContact.driveEfficiency < 0.7
  const directionOptions: { value: MotorDirection; label: string }[] = [
    { value: 'reverse', label: '역방향' },
    { value: 'stop', label: '정지' },
    { value: 'forward', label: '정방향' },
  ]
  const activeMotorControls = runtimeMotorControls ?? motorControls

  return (
    <aside className="inspector mode-sidebar">
      <PanelHeading
        title="선택한 로봇 제어"
        subtitle={selectedAssembly.length
          ? `부품 ${selectedAssembly.length}개 · 큐브 ${cubes.length}개`
          : '3D 화면에서 로봇을 선택하세요'}
      />

      {!selectedAssembly.length ? (
        <div className="robot-selection-empty">
          <span aria-hidden="true">◎</span>
          <strong>선택한 로봇이 없습니다</strong>
          <p>3D 화면에서 로봇 부품을 클릭하면 연결된 부품을 하나의 로봇으로 묶어 제어 정보와 분석 결과를 표시합니다.</p>
        </div>
      ) : (
        <>

      <section className="motor-controls" aria-label="선택한 로봇 큐브 모터 설정">
        <div className="motor-controls-heading">
          <div><strong>큐브 모터</strong><span>조립 순서 기준</span></div>
          <small>{drives.some((drive) => drive.speedMultiplier > 1) ? '큐브 180 rpm · MD-01 ×4' : '큐브 최대 180 rpm'}</small>
        </div>
        {cubes.map((cube, index) => {
          const control = motorControlFor(activeMotorControls, cube.instanceId)
          const drive = drivesByCube.get(cube.instanceId)
          const drivenPart = drive ? drivenParts.get(drive.drivenInstanceId) : null
          const drivenDefinition = drivenPart ? getPartById(drivenPart.partId) : null
          const running = isSimulating && control.direction !== 'stop' && control.speedPercent > 0 && drive
          return (
            <article className={`motor-control-card ${running ? 'running' : ''}`} key={cube.instanceId}>
              <header>
                <div>
                  <b>{index + 1}번 큐브</b>
                  <span>{drivenDefinition
                    ? drive && drive.speedMultiplier > 1
                      ? `MD-01 ×${drive.speedMultiplier} · ${drivenDefinition.name} 연결`
                      : `${drivenDefinition.name} 연결`
                    : '회전 부품 없음'}</span>
                </div>
                <em>{running ? '회전 중' : '대기'}</em>
              </header>
              <div className="motor-directions" aria-label={`${index + 1}번 큐브 회전 방향`}>
                {directionOptions.map((option) => (
                  <button
                    className={control.direction === option.value ? 'active' : ''}
                    key={option.value}
                    onClick={() => setMotorDirection(cube.instanceId, option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <label className="motor-speed">
                <span>속도</span>
                <input
                  aria-label={`${index + 1}번 큐브 속도`}
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={control.speedPercent}
                  onChange={(event) => setMotorSpeed(cube.instanceId, event.currentTarget.valueAsNumber)}
                />
                <output>{control.speedPercent}%</output>
              </label>
              <small className="motor-rpm">
                {control.direction === 'stop'
                  ? '출력 정지'
                  : drive && drive.speedMultiplier > 1
                    ? `모터 ${motorRpm(control)} rpm · 바퀴 ${motorOutputRpm(control, drive)} rpm · ${control.direction === 'forward' ? '정방향' : '역방향'}`
                    : `${motorRpm(control)} rpm 상당 · ${control.direction === 'forward' ? '정방향' : '역방향'}`}
              </small>
            </article>
          )
        })}
        {!cubes.length && <p className="motor-empty">선택한 로봇에 핑퐁 큐브가 없습니다.</p>}
      </section>

      <section className="simulation-analysis" aria-label="구조 및 접지 분석">
        <header><strong>구조·접지 분석</strong><span>선택한 로봇 기준</span></header>
        <div className="analysis-score">
          <span>결합 안정성</span>
          <strong>{Math.round(structure.assemblyScore * 100)}%</strong>
        </div>
        <div className="analysis-bar"><span style={{ width: `${structure.assemblyScore * 100}%` }} /></div>
        <dl>
          <div><dt>선택 로봇 추정 무게</dt><dd>{Math.round(groundContact.totalMassKg * 1000)}g</dd></div>
          <div><dt>보강 연결</dt><dd>{structure.reinforcedCount}개</dd></div>
          <div><dt>충격 취약 부품</dt><dd>{fragileNames.length ? fragileNames.join(', ') : '없음'}</dd></div>
          <div><dt>분리된 부품</dt><dd>{detachedCount}개</dd></div>
          <div><dt>바닥 지지점</dt><dd>{groundContact.supportLabels.join(' + ') || '없음'}</dd></div>
          <div><dt>주행 효율</dt><dd>{Math.round(groundContact.driveEfficiency * 100)}%</dd></div>
        </dl>
        <p className={conditionWarning ? 'analysis-warning' : 'analysis-ok'}>
          {conditionSummary}
        </p>
      </section>
        </>
      )}
    </aside>
  )
}
